import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Switch,
  FormControlLabel,
  Badge,
  Tooltip,
  CircularProgress,
  Alert,
  alpha,
  LinearProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Dashboard as DashboardIcon,
  LocalHospital as DoctorsIcon,
  CalendarMonth as AppointmentsIcon,
  Schedule as PendingIcon,
  CheckCircle as CompletedIcon,
  Warning as EmergencyIcon,
  MedicalServices as ActiveIcon,
  Cancel as CancelledIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  FiberManualRecord as DotIcon,
  Notifications as AlertIcon,
  DirectionsCar as TravelIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Assignment as ReportIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface DoctorStatus {
  id: number;
  name: string;
  specialization: string;
  status: string;
  statusColor: string;
  currentAppointment: string;
  currentAppointmentId: number;
  shiftTiming: string;
  completedToday: number;
  pendingCount: number;
  avgResponseTime: number;
}

interface AppointmentQueue {
  id: number;
  bookingNumber: string;
  patientName: string;
  officerName: string;
  officerCadre: string;
  doctorName: string;
  appointmentTime: string;
  status: string;
  priority: string;
  visitType: string;
  address: string;
  waitingMinutes: number;
  isEmergency: boolean;
}

interface ActiveVisit {
  appointmentId: number;
  bookingNumber: string;
  patientName: string;
  doctorName: string;
  startedAt: string;
  durationMinutes: number;
  address: string;
}

interface DelayedVisit {
  appointmentId: number;
  bookingNumber: string;
  patientName: string;
  doctorName: string;
  scheduledTime: string;
  delayMinutes: number;
  reason: string;
  officerContact: string;
}

interface CompletedCase {
  appointmentId: number;
  bookingNumber: string;
  patientName: string;
  doctorName: string;
  completedAt: string;
  summary: string;
  hasPrescription: boolean;
  requiresFollowUp: boolean;
}

interface RealTimeOperations {
  doctorStatuses: DoctorStatus[];
  appointmentQueue: AppointmentQueue[];
  activeVisits: ActiveVisit[];
  pendingConfirmations: any[];
  delayedVisits: DelayedVisit[];
  recentlyCompleted: CompletedCase[];
  lastUpdated: string;
}

interface OperationsSummary {
  totalActive: number;
  available: number;
  busy: number;
  onVisit: number;
  onLeave: number;
  offline: number;
  queueLength: number;
  delayedCount: number;
  completedCount: number;
}

/**
 * Real-Time Operations Center
 * Healthcare Command Center for Live Monitoring
 */
const RealTimeOperationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState<RealTimeOperations | null>(null);
  const [summary, setSummary] = useState<OperationsSummary | null>(null);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    doctors: true,
    queue: true,
    active: true,
    delayed: true,
    completed: false,
  });

  const fetchOperations = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Authentication required. Please login again.');
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/admin/dashboard/realtime`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        setOperations(data);

        // Calculate summary
        const doctors = data.doctorStatuses || [];
        setSummary({
          totalActive: doctors.length,
          available: doctors.filter((d: DoctorStatus) => d.status === 'AVAILABLE').length,
          busy: doctors.filter((d: DoctorStatus) => d.status === 'BUSY').length,
          onVisit: doctors.filter((d: DoctorStatus) => d.status === 'ON_VISIT').length,
          onLeave: doctors.filter((d: DoctorStatus) => d.status === 'LEAVE').length,
          offline: doctors.filter((d: DoctorStatus) => d.status === 'OFFLINE').length,
          queueLength: (data.appointmentQueue || []).length,
          delayedCount: (data.delayedVisits || []).length,
          completedCount: (data.recentlyCompleted || []).length,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load operations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN'].includes(user?.role || '')) {
      navigate('/login');
      return;
    }
    fetchOperations();
  }, [isAuthenticated, user, navigate, fetchOperations]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchOperations();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchOperations]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      AVAILABLE: '#10b981',
      BUSY: '#f59e0b',
      ON_VISIT: '#2563eb',
      LEAVE: '#ef4444',
      OFFLINE: '#6b7280',
    };
    return colors[status] || '#6b7280';
  };

  const getStatusBgColor = (status: string) => {
    const colors: Record<string, string> = {
      AVAILABLE: '#ecfdf5',
      BUSY: '#fffbeb',
      ON_VISIT: '#eff6ff',
      LEAVE: '#fef2f2',
      OFFLINE: '#f3f4f6',
    };
    return colors[status] || '#f3f4f6';
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading Real-Time Operations...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DotIcon sx={{ color: '#10b981', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } } }} />
            Real-Time Operations Center
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Live monitoring of healthcare operations • Auto-refresh: {autoRefresh ? `${refreshInterval}s` : 'Off'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="success"
              />
            }
            label={autoRefresh ? <Chip icon={<PlayIcon />} label="Live" color="success" size="small" /> : <Chip icon={<PauseIcon />} label="Paused" color="default" size="small" />}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { setLoading(true); fetchOperations(); }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>{summary.available}</Typography>
                <Typography variant="caption" color="text.secondary">Available</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>{summary.busy}</Typography>
                <Typography variant="caption" color="text.secondary">Busy</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2563eb' }}>{summary.onVisit}</Typography>
                <Typography variant="caption" color="text.secondary">On Visit</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>{summary.delayedCount}</Typography>
                <Typography variant="caption" color="text.secondary">Delayed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7c3aed' }}>{summary.queueLength}</Typography>
                <Typography variant="caption" color="text.secondary">In Queue</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#059669' }}>{summary.completedCount}</Typography>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left - Doctor Status */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ borderRadius: 2 }}>
            <Box
              sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', bgcolor: 'transparent' }}
              onClick={() => toggleSection('doctors')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DoctorsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Doctor Status Monitor</Typography>
                <Chip label={operations?.doctorStatuses?.length || 0} size="small" />
              </Box>
              {expandedSections.doctors ? <CollapseIcon /> : <ExpandIcon />}
            </Box>
            <Collapse in={expandedSections.doctors}>
              <List disablePadding>
                {operations?.doctorStatuses?.map((doctor, index) => (
                  <React.Fragment key={doctor.id}>
                    <ListItem sx={{ py: 2, bgcolor: getStatusBgColor(doctor.status), borderLeft: `4px solid ${getStatusColor(doctor.status)}` }}>
                      <ListItemIcon>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={<DotIcon sx={{ fontSize: 12, color: getStatusColor(doctor.status) }} />}
                        >
                          <Avatar sx={{ bgcolor: alpha(getStatusColor(doctor.status), 0.1), color: getStatusColor(doctor.status) }}>
                            <DoctorsIcon />
                          </Avatar>
                        </Badge>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{doctor.name}</Typography>
                            <Chip label={doctor.status} size="small" sx={{ bgcolor: getStatusColor(doctor.status), color: '#fff', fontWeight: 600 }} />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {doctor.specialization} • Shift: {doctor.shiftTiming || '09:00-17:00'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                              <Chip icon={<CompletedIcon />} label={`${doctor.completedToday} Done`} size="small" variant="outlined" />
                              <Chip icon={<PendingIcon />} label={`${doctor.pendingCount} Pending`} size="small" variant="outlined" />
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button size="small" variant="outlined" onClick={() => navigate(`/doctors/${doctor.id}`)}>
                          View
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < (operations?.doctorStatuses?.length || 0) - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {(!operations?.doctorStatuses || operations.doctorStatuses.length === 0) && (
                  <ListItem><ListItemText primary="No doctors found" sx={{ textAlign: 'center', py: 3 }} /></ListItem>
                )}
              </List>
            </Collapse>
          </Paper>

          {/* Active Visits */}
          <Paper sx={{ borderRadius: 2, mt: 3 }}>
            <Box
              sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', bgcolor: 'transparent' }}
              onClick={() => toggleSection('active')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="info" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Active Home Visits</Typography>
                <Chip label={operations?.activeVisits?.length || 0} size="small" color="info" />
              </Box>
              {expandedSections.active ? <CollapseIcon /> : <ExpandIcon />}
            </Box>
            <Collapse in={expandedSections.active}>
              <List disablePadding>
                {operations?.activeVisits?.map((visit, index) => (
                  <React.Fragment key={visit.appointmentId}>
                    <ListItem sx={{ py: 2 }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'transparent' , color: '#2563eb' }}>
                          <HomeIcon />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {visit.patientName} <Chip label={visit.bookingNumber} size="small" variant="outlined" />
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Doctor: {visit.doctorName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                              <Chip icon={<TimeIcon />} label={formatDuration(visit.durationMinutes)} size="small" color="primary" />
                              <Chip icon={<LocationIcon />} label={visit.address} size="small" variant="outlined" />
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button size="small" onClick={() => navigate(`/appointments/${visit.appointmentId}`)}>
                          Track
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < (operations?.activeVisits?.length || 0) - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {(!operations?.activeVisits || operations.activeVisits.length === 0) && (
                  <ListItem><ListItemText primary="No active visits" sx={{ textAlign: 'center', py: 3 }} /></ListItem>
                )}
              </List>
            </Collapse>
          </Paper>
        </Grid>

        {/* Right - Queue & Delayed */}
        <Grid item xs={12} lg={6}>
          {/* Appointment Queue */}
          <Paper sx={{ borderRadius: 2 }}>
            <Box
              sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', bgcolor: 'transparent' }}
              onClick={() => toggleSection('queue')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AppointmentsIcon sx={{ color: '#7c3aed' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Live Appointment Queue</Typography>
                <Chip label={operations?.appointmentQueue?.length || 0} size="small" color="secondary" />
              </Box>
              {expandedSections.queue ? <CollapseIcon /> : <ExpandIcon />}
            </Box>
            <Collapse in={expandedSections.queue}>
              <List disablePadding>
                {operations?.appointmentQueue?.map((appt, index) => (
                  <React.Fragment key={appt.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        bgcolor: appt.isEmergency ? alpha('#dc2626', 0.05) : 'inherit',
                        borderLeft: appt.isEmergency ? '4px solid #dc2626' : 'none',
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: appt.isEmergency ? '#dc2626' : '#7c3aed' }}>
                          {appt.isEmergency ? <EmergencyIcon /> : <AppointmentsIcon />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{appt.patientName}</Typography>
                            {appt.isEmergency && <Chip label="EMERGENCY" size="small" color="error" />}
                            {appt.waitingMinutes > 30 && <Chip label={`${appt.waitingMinutes}m wait`} size="small" color="warning" />}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {appt.officerName} ({appt.officerCadre})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip label={`Dr. ${appt.doctorName}`} size="small" variant="outlined" />
                              <Chip label={formatTime(appt.appointmentTime)} size="small" icon={<TimeIcon />} />
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button size="small" onClick={() => navigate(`/appointments/${appt.id}`)}>
                          View
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < (operations?.appointmentQueue?.length || 0) - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {(!operations?.appointmentQueue || operations.appointmentQueue.length === 0) && (
                  <ListItem><ListItemText primary="Queue is empty" sx={{ textAlign: 'center', py: 3 }} /></ListItem>
                )}
              </List>
            </Collapse>
          </Paper>

          {/* Delayed Visits */}
          <Paper sx={{ borderRadius: 2, mt: 3 }}>
            <Box
              sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', bgcolor: 'transparent' }}
              onClick={() => toggleSection('delayed')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmergencyIcon color="error" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626' }}>Delayed Visits</Typography>
                <Badge badgeContent={operations?.delayedVisits?.length || 0} color="error" />
              </Box>
              {expandedSections.delayed ? <CollapseIcon /> : <ExpandIcon />}
            </Box>
            <Collapse in={expandedSections.delayed}>
              <List disablePadding>
                {operations?.delayedVisits?.map((visit, index) => (
                  <React.Fragment key={visit.appointmentId}>
                    <ListItem sx={{ py: 2, bgcolor: alpha('#dc2626', 0.02) }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'transparent' , color: '#dc2626' }}>
                          <EmergencyIcon />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{visit.patientName}</Typography>
                            <Chip label={`${visit.delayMinutes}m DELAYED`} size="small" color="error" />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Doctor: {visit.doctorName} • Scheduled: {formatTime(visit.scheduledTime)}
                            </Typography>
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              Reason: {visit.reason || 'Doctor not arrived'}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button size="small" color="error" startIcon={<PhoneIcon />}>
                          Call
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < (operations?.delayedVisits?.length || 0) - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {(!operations?.delayedVisits || operations.delayedVisits.length === 0) && (
                  <ListItem><ListItemText primary="No delayed visits ✅" sx={{ textAlign: 'center', py: 3 }} /></ListItem>
                )}
              </List>
            </Collapse>
          </Paper>

          {/* Recently Completed */}
          <Paper sx={{ borderRadius: 2, mt: 3 }}>
            <Box
              sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', bgcolor: 'transparent' }}
              onClick={() => toggleSection('completed')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompletedIcon sx={{ color: '#10b981' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Recently Completed</Typography>
                <Chip label={operations?.recentlyCompleted?.length || 0} size="small" color="success" />
              </Box>
              {expandedSections.completed ? <CollapseIcon /> : <ExpandIcon />}
            </Box>
            <Collapse in={expandedSections.completed}>
              <List disablePadding>
                {operations?.recentlyCompleted?.map((item, index) => (
                  <React.Fragment key={item.appointmentId}>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'transparent' , color: '#10b981' }}>
                          <CompletedIcon />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {item.patientName} • {item.doctorName}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {item.hasPrescription && <Chip label="Prescription" size="small" icon={<ReportIcon />} />}
                            {item.requiresFollowUp && <Chip label="Follow-Up" size="small" color="warning" />}
                            <Typography variant="caption">{item.summary?.substring(0, 50)}...</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < (operations?.recentlyCompleted?.length || 0) - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {(!operations?.recentlyCompleted || operations.recentlyCompleted.length === 0) && (
                  <ListItem><ListItemText primary="No completed visits" sx={{ textAlign: 'center', py: 3 }} /></ListItem>
                )}
              </List>
            </Collapse>
          </Paper>
        </Grid>
      </Grid>

      {/* Last Updated */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {operations?.lastUpdated ? new Date(operations.lastUpdated).toLocaleString() : '--'}
        </Typography>
      </Box>
    </Box>
  );
};

export default RealTimeOperationsPage;
